import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

interface CompareSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel: string;
  afterLabel: string;
  name: string;
}

export default function CompareSlider({
  beforeImage,
  afterImage,
  beforeLabel,
  afterLabel,
  name,
}: CompareSliderProps) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, next)));
  }, []);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    updateFromClientX(event.clientX);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateFromClientX(event.clientX);
  };

  const stopDragging = () => {
    draggingRef.current = false;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      setPosition((prev) => Math.max(0, prev - 4));
    } else if (event.key === "ArrowRight") {
      setPosition((prev) => Math.min(100, prev + 4));
    }
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerLeave={stopDragging}
      className="relative w-full touch-pan-y select-none overflow-hidden rounded-lg border border-background-200 bg-background-200"
    >
      <div className="relative aspect-[4/5] w-full sm:aspect-[16/11]">
        <img
          src={afterImage}
          alt={`${name} 近期形象`}
          title={`${name} 近期形象对比`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        >
          <img
            src={beforeImage}
            alt={`${name} 早期形象`}
            title={`${name} 早期形象对比`}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
        </div>

        <span className="absolute left-3 top-3 rounded-full bg-foreground-950/70 px-3 py-1 text-xs font-medium text-background-50">
          {beforeLabel}
        </span>
        <span className="absolute right-3 top-3 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
          {afterLabel}
        </span>

        <div
          className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-background-50"
          style={{ left: `${position}%` }}
        >
          <button
            type="button"
            role="slider"
            aria-label="拖动查看形象变化"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(position)}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border-2 border-background-50 bg-primary-500 text-background-50 outline-none focus:ring-2 focus:ring-accent-300"
          >
            <i className="ri-drag-move-2-line text-lg"></i>
          </button>
        </div>
      </div>

      <p className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-foreground-950/60 px-3 py-1 text-[11px] text-background-50">
        拖动中间按钮，左右滑动对比
      </p>
    </div>
  );
}